import React from 'react'

/**
 * PlayerRemoveModal - Confirmation modal for removing a player from a league
 *
 * @param {Object} props
 * @param {boolean} props.show - Whether the modal is visible
 * @param {Object} props.player - Player object to be removed
 * @param {boolean} props.isRemoving - Whether removal is in progress
 * @param {Function} props.onClose - Handler to close modal
 * @param {Function} props.onConfirm - Handler when removal is confirmed
 * @returns {JSX.Element}
 */
function PlayerRemoveModal({ show, player, isRemoving, onClose, onConfirm }) {
  if (!show || !player) {
    return null
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && !isRemoving) {
      onClose()
    }
  }

  return (
    <div
      className="modal show d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      role="dialog"
      aria-labelledby="remove-player-modal-title"
      aria-describedby="remove-player-modal-description"
      aria-modal="true"
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="remove-player-modal-title">
              <i className="fas fa-user-minus me-2"></i>
              Remove Player from League
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={isRemoving}
              aria-label="Close modal"
            ></button>
          </div>

          <div className="modal-body">
            <p id="remove-player-modal-description">
              Are you sure you want to remove{' '}
              <strong>
                {player.first_name} {player.last_name}
              </strong>{' '}
              from this league?
            </p>
            <div className="alert alert-info" role="alert">
              <h6 className="alert-heading">
                <i className="fas fa-info-circle me-2"></i>
                What happens next:
              </h6>
              <ul className="mb-0">
                <li>Player will be removed from the league registration</li>
                <li>If assigned to a division, they will be unassigned</li>
                <li>Player can re-register for the league later if needed</li>
                <li>Registration history will be maintained for auditing</li>
              </ul>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isRemoving}
              aria-label="Cancel removal"
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={onConfirm}
              disabled={isRemoving}
              aria-label="Confirm player removal"
            >
              {isRemoving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Removing...
                </>
              ) : (
                <>
                  <i className="fas fa-user-minus me-2"></i>
                  Remove Player
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PlayerRemoveModal
